import React, { useState, useEffect, useCallback } from "react";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import { ArrowLeft, Camera, X, ChevronLeft, ChevronRight } from "lucide-react";
import { Link } from "react-router-dom";

const IMAGES_PER_PAGE = 24;

// Generate image list from local files: BuiltToLead-.jpg, BuiltToLead- 1.jpg ... BuiltToLead- 281.jpg
const galleryImages = [
  { id: 0, src: "/gallery/BuiltToLead-.jpg", alt: "Built to Lead Ottawa" },
  ...Array.from({ length: 281 }, (_, i) => ({
    id: i + 1,
    src: `/gallery/BuiltToLead- ${i + 1}.jpg`,
    alt: `Built to Lead Ottawa ${i + 1}`,
  })),
];

export default function GalleryPage() {
  const [visibleCount, setVisibleCount] = useState(IMAGES_PER_PAGE);
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const visibleImages = galleryImages.slice(0, visibleCount);
  const hasMore = visibleCount < galleryImages.length;
  const lightboxOpen = lightboxIndex !== null;

  const goNext = useCallback(() => {
    setLightboxIndex((prev) => (prev + 1) % galleryImages.length);
  }, []);

  const goPrev = useCallback(() => {
    setLightboxIndex((prev) => (prev - 1 + galleryImages.length) % galleryImages.length);
  }, []);

  const closeLightbox = useCallback(() => {
    setLightboxIndex(null);
  }, []);

  useEffect(() => {
    if (!lightboxOpen) return;
    const handleKey = (e) => {
      if (e.key === "Escape") closeLightbox();
      if (e.key === "ArrowRight") goNext();
      if (e.key === "ArrowLeft") goPrev();
    };
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", handleKey);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", handleKey);
    };
  }, [lightboxOpen, closeLightbox, goNext, goPrev]);

  return (
    <div className="min-h-screen" style={{ background: "#ffffff", fontFamily: "'Inter', Arial, sans-serif" }}>
      <Header />
      <main className="pt-28 md:pt-36 pb-20">
        <div className="max-w-[87.5rem] mx-auto px-3 md:px-10">
          {/* Back link */}
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-[#6b7280] hover:text-[#FE3A80] transition-colors duration-300 no-underline text-sm font-medium mb-8"
            style={{ fontFamily: "'Inter', Arial, sans-serif" }}
          >
            <ArrowLeft size={16} />
            Back to Home
          </Link>

          {/* Section Header */}
          <div className="mb-12">
            <div className="flex items-center gap-3 mb-4">
              <Camera size={20} className="text-[#FE3A80]" />
              <span
                className="text-xs font-medium uppercase tracking-[0.2em] text-[#6b7280]"
                style={{ fontFamily: "'Inter', Arial, sans-serif" }}
              >
                BUILT TO LEAD 2026
              </span>
            </div>
            <h1
              className="text-[#FE3A80] uppercase leading-[0.85] mb-6"
              style={{
                fontFamily: "'Montserrat', Arial, sans-serif",
                fontWeight: 900,
                fontSize: "clamp(2rem, 2.5rem + 2vw, 4.5rem)",
              }}
            >
              Event Gallery
            </h1>
            <p
              className="text-[#6b7280] text-lg max-w-2xl"
              style={{ fontFamily: "'Inter', Arial, sans-serif" }}
            >
              Relive the moments from Built to Lead Ottawa. Browse through highlights from our inspiring evening of leadership and empowerment.
            </p>
          </div>

          {/* Masonry Grid */}
          <div className="bento-grid">
            {visibleImages.map((image) => (
              <div
                key={image.id}
                className="group relative overflow-hidden bg-[#f3f4f6] cursor-pointer"
                onClick={() => setLightboxIndex(image.id)}
              >
                <img
                  src={image.src}
                  alt={image.alt}
                  className="w-full h-auto block transition-transform duration-700 group-hover:scale-110"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              </div>
            ))}
          </div>

          {/* Load More */}
          {hasMore && (
            <div className="text-center mt-12">
              <button
                onClick={() => setVisibleCount((prev) => prev + IMAGES_PER_PAGE)}
                className="inline-flex items-center justify-center bg-[#FE3A80] text-[#ffffff] border-none rounded-[10rem] px-8 py-4 text-sm font-semibold uppercase cursor-pointer transition-transform duration-300 hover:scale-[1.02] active:scale-[0.98]"
                style={{ fontFamily: "'Inter', Arial, sans-serif", letterSpacing: "-0.04em" }}
              >
                Load More ({galleryImages.length - visibleCount} remaining)
              </button>
            </div>
          )}
        </div>
      </main>
      <Footer />

      {/* Lightbox */}
      {lightboxOpen && (
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/90"
          onClick={closeLightbox}
        >
          {/* Close button */}
          <button
            onClick={closeLightbox}
            className="absolute top-4 right-4 z-10 bg-transparent border-none text-white cursor-pointer p-2 hover:opacity-70 transition-opacity"
          >
            <X size={28} />
          </button>

          {/* Prev button */}
          <button
            onClick={(e) => { e.stopPropagation(); goPrev(); }}
            className="absolute left-4 z-10 bg-transparent border-none text-white cursor-pointer p-2 hover:opacity-70 transition-opacity"
          >
            <ChevronLeft size={36} />
          </button>

          {/* Image */}
          <img
            src={galleryImages[lightboxIndex].src}
            alt={galleryImages[lightboxIndex].alt}
            className="max-h-[90vh] max-w-[90vw] object-contain"
            onClick={(e) => e.stopPropagation()}
          />

          {/* Next button */}
          <button
            onClick={(e) => { e.stopPropagation(); goNext(); }}
            className="absolute right-4 z-10 bg-transparent border-none text-white cursor-pointer p-2 hover:opacity-70 transition-opacity"
          >
            <ChevronRight size={36} />
          </button>

          {/* Counter */}
          <span
            className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white/70 text-sm"
            style={{ fontFamily: "'Inter', Arial, sans-serif" }}
          >
            {lightboxIndex + 1} / {galleryImages.length}
          </span>
        </div>
      )}
    </div>
  );
}
